// CSV export and the import plan (docs/design/csv-import-export.md). Export runs
// here from the grid's state; import is parsed and checked on the server, and
// this decides where the accepted rows land.
import type { TimesheetRow } from '../api/client';
import { targetKey, type BillingTarget, type DraftRow } from '../api/draft';

const COLUMNS = ['date', 'project', 'phase', 'task', 'category', 'hours', 'comment', 'source', 'project_name', 'phase_name', 'task_name'];

interface ExportLine {
  dayIndex: number;
  source: 'vp' | 'draft';
  project: string; phase: string; task: string; category: string;
  hours: number;
  comments: string[];
  projectName: string; phaseName: string; taskName: string;
}

export interface ExportInput {
  periodStart: string;
  vpRows: TimesheetRow[];
  draftRows: DraftRow[];
}

// What the grid shows: VP-saved cells as source=vp rows and the draft cells
// under "Your draft" as source=draft rows. A draft cell already written to VP is
// shown as the VP row it became, so it is left out here too. VP rows that repeat
// a target (Bug 5956) sum into one vp row per day; import ignores vp rows, so
// the sum is only there to be read.
export function exportCsv({ periodStart, vpRows, draftRows }: ExportInput): string {
  const lines = new Map<string, ExportLine>();
  const add = (dayIndex: number, t: Omit<ExportLine, 'dayIndex' | 'hours' | 'comments'>, hours: number, comment: string) => {
    const key = `${t.source}|${dayIndex}|${t.category ? `category:${t.category}` : `${t.project}|${t.phase}|${t.task}`}`;
    const line = lines.get(key) ?? { dayIndex, ...t, hours: 0, comments: [] };
    line.hours = Math.round((line.hours + hours) * 100) / 100;
    if (comment && !line.comments.includes(comment)) line.comments.push(comment);
    lines.set(key, line);
  };

  for (const row of vpRows) {
    const t = row.isCategory
      ? { source: 'vp' as const, project: '', phase: '', task: '', category: row.category, projectName: '', phaseName: '', taskName: '' }
      : {
          source: 'vp' as const, project: row.wbs1, phase: row.wbs2, task: row.wbs3, category: '',
          projectName: row.wbs1Name, phaseName: row.wbs2Name, taskName: row.wbs3Name,
        };
    for (const cell of row.days) {
      if (cell.total > 0) add(cell.index - 1, t, cell.total, cell.comment);
    }
  }

  for (const row of draftRows) {
    const t = row.target.category
      ? { source: 'draft' as const, project: '', phase: '', task: '', category: row.target.category, projectName: '', phaseName: '', taskName: '' }
      : {
          source: 'draft' as const, project: row.target.wbs1, phase: row.target.wbs2, task: row.target.wbs3, category: '',
          projectName: row.target.wbs1Name, phaseName: row.target.wbs2Name, taskName: row.target.wbs3Name,
        };
    for (const day of row.days) {
      if (!day.written && day.hours > 0) add(day.dayIndex, t, day.hours, day.note.text);
    }
  }

  const body = [...lines.values()]
    .sort((a, b) => a.dayIndex - b.dayIndex)
    .map(l => [
      isoDay(periodStart, l.dayIndex), l.project, l.phase, l.task, l.category, String(l.hours),
      l.comments.join('; '), l.source, l.projectName, l.phaseName, l.taskName,
    ].map(csvField).join(','));
  return [COLUMNS.join(','), ...body].join('\r\n') + '\r\n';
}

// A file to fill in: the header, one project row and one category row. The
// date placeholder fails the import's date check, so a template imported
// unchanged reports both rows instead of booking them.
export function templateCsv(): string {
  return [
    COLUMNS.join(','),
    'YYYY-MM-DD,PROJECT-CODE,PHASE-CODE,TASK-CODE,,1.5,Example project row,draft,,,',
    'YYYY-MM-DD,,,,CATEGORY-CODE,8,Example category row,draft,,,',
  ].join('\r\n') + '\r\n';
}

// A leading = + - @ tab or CR makes a spreadsheet evaluate the cell; the ' keeps
// it text. The import strips exactly this prefix back off.
function csvField(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function isoDay(periodStart: string, dayIndex: number): string {
  const [y, m, d] = periodStart.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + dayIndex)).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Import plan
// ---------------------------------------------------------------------------

export interface ImportedEntry {
  line: number;
  date: string;
  dayIndex: number;
  target: BillingTarget;
  hours: number;
  comment: string;
}

export interface ImportError { line: number; column: string | null; message: string; }

// ignoredVpRows counts the file's source=vp rows: what VP already holds, never imported.
export interface ImportResponse { entries: ImportedEntry[]; errors: ImportError[]; ignoredVpRows: number; }

export interface ImportPlan {
  // Rows that will be booked (subject to Skip / Replace on draft clashes).
  importing: ImportedEntry[];
  // Draft cells the import lands on, rolled up per target.
  existing: { key: string; label: string; days: number; hours: number }[];
  // Rows landing on cells VP already holds hours for. Never booked (ADR-0041).
  inVp: { line: number; label: string; date: string; fileHours: number; vpHours: number }[];
}

function targetLabel(t: BillingTarget): string {
  return [t.wbs1Name || t.wbs1, t.wbs2Name || t.wbs2, t.wbs3Name || t.wbs3].filter(Boolean).join(' · ');
}

// The hours VP holds for this entry's cell. A category entry matches its band
// row by code; a project entry matches every non-category row of its WBS path
// (VP allows several, Bug 5956).
function vpHoursFor(entry: ImportedEntry, vpRows: TimesheetRow[]): number {
  const t = entry.target;
  const matches = vpRows.filter(r => t.category
    ? r.isCategory && r.category === t.category
    : !r.isCategory && r.wbs1 === t.wbs1 && r.wbs2 === t.wbs2 && r.wbs3 === t.wbs3);
  return matches.reduce((sum, r) => sum + (r.days.find(d => d.index === entry.dayIndex + 1)?.total ?? 0), 0);
}

export function planImport(entries: ImportedEntry[], draftRows: DraftRow[], vpRows: TimesheetRow[]): ImportPlan {
  const importing: ImportedEntry[] = [];
  const inVp: ImportPlan['inVp'] = [];
  const existing = new Map<string, ImportPlan['existing'][number]>();

  for (const entry of entries) {
    const vpHours = vpHoursFor(entry, vpRows);
    if (vpHours > 0) {
      inVp.push({ line: entry.line, label: targetLabel(entry.target), date: entry.date, fileHours: entry.hours, vpHours });
      continue;
    }
    importing.push(entry);
    const key = targetKey(entry.target);
    const cell = draftRows.find(r => targetKey(r.target) === key)?.days.find(d => d.dayIndex === entry.dayIndex);
    if (!cell) continue;
    const group = existing.get(key) ?? { key, label: targetLabel(entry.target), days: 0, hours: 0 };
    group.days += 1;
    group.hours = Math.round((group.hours + cell.hours) * 100) / 100;
    existing.set(key, group);
  }

  return { importing, existing: [...existing.values()], inVp };
}
